import { useEffect, useMemo } from "react";
import { isAndroidWebView } from "utilities/display";
import { getBridge } from "utilities/bridge";
import { useProfile } from "@/hooks/firestore/user/useProfile";
import { useFiatCurrency } from "@/hooks/firestore/user/useFiatCurrency";
import { useWalletStore } from "@/features/wallet/stores/walletStore";
import { walletService } from "@/features/wallet/services/wallet";
import { parseError } from "utilities/errorsParser";

type GooglePayHook = {
  isFinal: boolean;
  amount: number;
  loading: boolean;
  onSuccess?: () => void;
};

const useGooglePay = ({
  isFinal,
  loading,
  amount,
  onSuccess,
}: GooglePayHook) => {
  const currency = useFiatCurrency();

  const setGoogleDepositStatus = useWalletStore(
    (state) => state.setGoogleDepositStatus,
  );
  const googleDepositStatus = useWalletStore(
    (state) => state.googleDepositStatus,
  );
  const setGoogleDepositError = useWalletStore(
    (state) => state.setGoogleDepositError,
  );

  const hasDeposited = googleDepositStatus === "success";

  // We reset the successful state when the user visits the page
  useEffect(() => {
    if (hasDeposited) {
      setGoogleDepositStatus("ready");
      setGoogleDepositError(null);
      onSuccess?.();
    }
  }, [hasDeposited]);

  const { profile } = useProfile();

  const country = profile?.country;

  const paymentRequest = useMemo(
    () =>
      ({
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [
          {
            type: "CARD",
            parameters: {
              // We add this logic to allow the loading of the gPay button in the webview
              // as the webview does not support the CRYPTOGRAM_3DS auth method
              // We however can assume that if the user has managed to get to this page, there the App has already sent the message to the webview
              // to enable the gPay button, so we can safely assume that the webview supports the CRYPTOGRAM_3DS auth method
              // see AppToWebEvents.tsx (Line 85)
              allowedAuthMethods: isAndroidWebView()
                ? ["PAN_ONLY", "CRYPTOGRAM_3DS"]
                : ["CRYPTOGRAM_3DS"],
              //Limit to mastercard only as there is no liability shift for Visa over GPay event with CRYPTOGRAM_3DS
              allowedCardNetworks: ["MASTERCARD"],
            },
            tokenizationSpecification: {
              type: "PAYMENT_GATEWAY",
              parameters: {
                gateway: "merchantwarrior",
                gatewayMerchantId: process.env.GATSBY_MERCHANTWARRIOR_MERCHANT,
              },
            },
          },
        ],
        merchantInfo: {
          merchantId: process.env.GATSBY_GOOGLE_PAY_MERCHANT_ID,
          merchantName: "Picklebet",
        },
        transactionInfo: {
          totalPriceStatus: isFinal ? "FINAL" : "ESTIMATED",
          totalPriceLabel: "Total",
          totalPrice: `${amount}` || "0",
          currencyCode: currency,
          countryCode: country,
        },
      }) as google.payments.api.PaymentDataRequest,
    [amount, isFinal, currency, country],
  );

  const deposit = async (paymentToken?: string, panSuffix?: string) => {
    setGoogleDepositError(null);

    if (loading) return;

    if (isAndroidWebView()) {
      // The native app renders the Google Pay sheet. We must NOT enter the
      // processing state here: if the user dismisses the sheet the app sends
      // nothing back, which would leave us stuck on "processing" forever.
      // Processing is set once a token actually returns (see BridgeEventHandler).
      getBridge().send.googlePay({ amount });
      return;
    }

    setGoogleDepositStatus("processing");

    const result = await walletService.merchantWarriorDeposit({
      digitalWalletToken: paymentToken,
      amount,
      digitalWalletType: "Google",
      panSuffix: panSuffix ?? "",
    });

    if (!result.ok) {
      setGoogleDepositError(parseError(result.error));
    } else if (result.data === "success") {
      setGoogleDepositStatus("success");
      return;
    }

    setGoogleDepositStatus("ready");
  };

  return {
    hasDeposited,
    deposit,
    paymentRequest,
  };
};

export { useGooglePay };
